import { Timestamp } from "@angular/fire/firestore";

// =============================================================================
// Firestore data model — Contadores RV
// =============================================================================
// Naming: collections are referenced via FirestoreService.col(name) which
// auto-prepends the env prefix ("dev-" or ""). Never hard-code a collection
// name with the prefix in components.
//
// Convention:
//   - Doc IDs are strings.
//     - users/{uid}    → uid is the Firebase Auth UID (stable).
//     - clients/{auto} → auto-generated by Firestore.
//     - bills/{auto}, billsAuto/{auto}, payments/{auto} → auto-generated.
//   - Server timestamps via serverTimestamp(); we store and read as Timestamp.
//   - Money is stored as number (decimal). Currency is always Costa-Rica
//     colones (CRC) — multi-currency is intentionally out of scope.
//   - Nested value-objects (the legacy Person/Address/Contact bags) were
//     dropped: identity fields live directly on `clients/{id}` and on
//     `users/{uid}`. See `legacy-mappers.ts` for `buildClientSnapshot` only.
// =============================================================================

// ---- Top-level documents ----------------------------------------------------

/**
 * users/{uid} — fusion of legacy SQL Person + GoogleAuth + User.
 *
 * Identification (name, lastname, documentId, ...) is stored flat — no
 * nested `person` object. The single role we model is SuperAdmin, kept as a
 * boolean for cheap rule checks; richer permission models can be added later.
 */
export interface AppUser {
  id: string;                    // === Firebase Auth UID
  email: string;
  givenName?: string | null;
  familyName?: string | null;
  picture?: string | null;

  // Identity (flat) ---------------------------------------------------------
  name?: string | null;          // legal name (matches Registro Civil)
  lastname?: string | null;
  documentId?: string | null;    // digits-only string
  birthdate?: Timestamp | null;
  personType?: number | null;    // Hacienda's identification type code

  // Contact (flat) ----------------------------------------------------------
  phoneNumber?: string | null;

  // Preferences -------------------------------------------------------------
  acceptTerms?: boolean;
  notifyMail: boolean;
  darkMode: boolean;

  // Authorization -----------------------------------------------------------
  /**
   * Mirrored on Firebase Auth custom claim `role: "SuperAdmin"` whenever
   * possible (cheaper for rule evaluation), but the field is the source of
   * truth so the bootstrap flow can run without admin SDK tooling.
   */
  isSuperAdmin: boolean;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * clients/{id} — the accountant's customer.
 *
 * Identity fields are stored flat. `companyNameLower` exists so we can do
 * case-insensitive prefix searches with a single Firestore index. Totals
 * (`totalBalance`, `unpaidCount`) are denormalized so reports don't have to
 * iterate over bills.
 */
export interface Client {
  id: string;
  creatorUid: string;            // → users/{uid}
  creatorName: string;           // denormalized for table display

  // Business profile --------------------------------------------------------
  companyName: string;
  companyNameLower: string;
  agent?: string | null;
  category?: string | null;
  additionalInfo?: string | null;

  // Identity (flat) ---------------------------------------------------------
  name?: string | null;          // legal name (or trade name for companies)
  lastname?: string | null;
  documentId?: string | null;    // digits-only string
  birthdate?: Timestamp | null;
  personType?: number | null;    // 1 = natural person, 2 = company

  // Contact (flat) ----------------------------------------------------------
  phoneNumber?: string | null;
  email?: string | null;
  /**
   * Free-text address description. Kept simple — no lat/lng or googleCode
   * because the previous structure was never populated in production.
   */
  address?: string | null;

  // Denormalized totals (kept in sync via FieldValue.increment) -------------
  totalBalance: number;
  unpaidCount: number;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/** Snapshot of a client embedded inside a Bill for display without JOINs. */
export interface ClientSnapshot {
  id: string;
  companyName: string;
  companyNameLower: string;
  /** First / legal given name (natural person); mirrors `Client.name`. */
  name?: string | null;
  lastname?: string | null;
  personType?: number | null;
  documentId?: string | null;
  category?: string | null;
}

/**
 * bills/{id} — invoices the accountant manages on behalf of a client.
 */
export interface Bill {
  id: string;
  /** Human-friendly invoice number shown in UI/reports. */
  consecutive?: number;
  clientId: string;
  creatorUid: string;
  creatorName: string;
  client: ClientSnapshot;        // denormalized client info

  mount: number;
  balance: number;               // remaining unpaid amount
  paid: number;                  // 0 = unpaid, 1 = fully paid, 2 = annulled
  notes: string | null;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * billsAuto/{id} — recurring monthly bill template.
 */
export interface BillAuto {
  id: string;
  /** Day of month when the cron should generate this invoice (1..28). */
  generationDay?: number;
  clientId: string;
  creatorUid: string;
  creatorName: string;
  client: ClientSnapshot;
  mount: number;
  notes: string | null;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * payments/{id} — partial payment applied to a bill.
 */
export interface Payment {
  id: string;
  billId: string;
  clientId: string;              // denormalized for queries by client
  creatorUid: string;
  creatorName: string;
  mount: number;
  notes: string | null;
  fileUrl: string | null;        // receipt download URL from Firebase Storage
  fileName: string | null;
  electronicBill: string | null; // "clave numérica" of the e-invoice
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// =============================================================================
// Collection names (without prefix — FirestoreService.col() handles the prefix)
// =============================================================================

export const COL = {
  users: "users",
  clients: "clients",
  bills: "bills",
  counters: "counters",
  billsAuto: "billsAuto",
  payments: "payments",
} as const;

export type CollectionName = (typeof COL)[keyof typeof COL];
